import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, Loader, Filter } from 'lucide-react';
import {
  getBulkMatchRun,
  getLatestBulkMatchRun,
  listBulkMatchItems,
  applyBulkMatchReview,
  skipBulkMatchItem,
  applyBulkMatchByConfidence,
  BulkMatchReviewItem,
  BulkMatchRun,
  MatchConfidence,
} from '../../../utils/nutritionApi';

const CONFIDENCE_LABELS: Record<MatchConfidence, string> = {
  high: 'Élevée',
  medium: 'Moyenne',
  low: 'Faible',
  none: 'Aucune',
};

const CONFIDENCE_STYLES: Record<MatchConfidence, string> = {
  high: 'bg-green-100 text-green-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-orange-100 text-orange-800',
  none: 'bg-gray-100 text-gray-600',
};

export function BulkMatchReviewPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const runIdParam = searchParams.get('runId');

  const [run, setRun] = useState<BulkMatchRun | null>(null);
  const [items, setItems] = useState<BulkMatchReviewItem[]>([]);
  const [confidenceFilter, setConfidenceFilter] = useState<MatchConfidence | ''>('');
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async (preferredRunId?: string | null) => {
    setLoading(true);
    setError(null);
    try {
      let nextRun: BulkMatchRun;
      if (preferredRunId) {
        const res = await getBulkMatchRun(preferredRunId);
        nextRun = res.run;
      } else {
        const res = await getLatestBulkMatchRun();
        nextRun = res.run;
      }
      setRun(nextRun);
      const itemsRes = await listBulkMatchItems(nextRun.id, {
        confidence: confidenceFilter || undefined,
        status: 'pending',
      });
      setItems(itemsRes.items);
      const defaults: Record<string, string> = {};
      for (const item of itemsRes.items) {
        if (item.suggestedCiqualCode != null) {
          defaults[item.ingredientId] = String(item.suggestedCiqualCode);
        }
      }
      setSelections(defaults);
    } catch (err) {
      setError((err as Error).message);
      setRun(null);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(runIdParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runIdParam]);

  useEffect(() => {
    if (!run?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const itemsRes = await listBulkMatchItems(run.id, {
          confidence: confidenceFilter || undefined,
          status: 'pending',
        });
        if (cancelled) return;
        setItems(itemsRes.items);
        setSelections((prev) => {
          const next = { ...prev };
          for (const item of itemsRes.items) {
            if (next[item.ingredientId] == null && item.suggestedCiqualCode != null) {
              next[item.ingredientId] = String(item.suggestedCiqualCode);
            }
          }
          return next;
        });
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [confidenceFilter, run?.id]);

  const withBusy = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const counts = useMemo(() => run?.byConfidence || null, [run]);

  const applyOne = (item: BulkMatchReviewItem) =>
    withBusy(`apply-${item.ingredientId}`, async () => {
      if (!run) return;
      const code = selections[item.ingredientId];
      if (!code) throw new Error('Choisissez un candidat CIQUAL');
      await applyBulkMatchReview(item.ingredientId, Number(code), run.id);
      setItems((prev) => prev.filter((x) => x.ingredientId !== item.ingredientId));
    });

  const skipOne = (item: BulkMatchReviewItem) =>
    withBusy(`skip-${item.ingredientId}`, async () => {
      if (!run) return;
      await skipBulkMatchItem(run.id, item.ingredientId);
      setItems((prev) => prev.filter((x) => x.ingredientId !== item.ingredientId));
    });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/admin/mass-db/ciqual"
            className="inline-flex items-center text-sm text-gray-500 hover:text-menioo-navy mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Retour CIQUAL
          </Link>
          <h2 className="text-xl font-display font-bold text-menioo-navy">
            Review matching CIQUAL
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Validez ou ajustez chaque proposition. Filtrez par niveau de certitude.
          </p>
        </div>
        {run && (
          <button
            type="button"
            disabled={!!busy}
            onClick={() => {
              if (
                !window.confirm(
                  `Appliquer la suggestion pour tous les items à certitude élevée encore en attente ?`
                )
              ) {
                return;
              }
              withBusy('applyHigh', async () => {
                const res = await applyBulkMatchByConfidence(run.id, 'high');
                await load(run.id);
                if (res.failed) {
                  setError(`${res.applied} appliqués, ${res.failed} échecs`);
                }
              });
            }}
            className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white bg-menioo-green hover:bg-menioo-green-dark disabled:opacity-50"
          >
            {busy === 'applyHigh' ? (
              <Loader className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            Appliquer certitude élevée
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center text-sm text-gray-500">
          <Loader className="h-4 w-4 mr-2 animate-spin" />
          Chargement…
        </div>
      ) : !run ? (
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-sm text-gray-600">
          Aucun matching trouvé.{' '}
          <button
            type="button"
            className="text-menioo-green underline"
            onClick={() => navigate('/admin/mass-db/ciqual')}
          >
            Lancer un matching
          </button>
        </div>
      ) : (
        <>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm grid grid-cols-2 sm:grid-cols-5 gap-2">
            <div>
              Run : <span className="font-mono text-xs">{run.id.slice(0, 8)}…</span>
            </div>
            <div>
              File : <strong>{run.queued ?? '—'}</strong>
            </div>
            {counts &&
              (Object.keys(CONFIDENCE_LABELS) as MatchConfidence[]).map((key) => (
                <div key={key}>
                  {CONFIDENCE_LABELS[key]} : <strong>{counts[key] ?? 0}</strong>
                </div>
              ))}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-menioo-navy">
              <Filter className="h-4 w-4 text-gray-400" />
              Certitude
              <select
                value={confidenceFilter}
                onChange={(e) => setConfidenceFilter(e.target.value as MatchConfidence | '')}
                className="rounded-md border-gray-300 text-sm"
              >
                <option value="">Toutes</option>
                <option value="high">Élevée</option>
                <option value="medium">Moyenne</option>
                <option value="low">Faible</option>
                <option value="none">Aucune</option>
              </select>
            </label>
            <span className="text-sm text-gray-500">{items.length} en attente</span>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full table-fixed divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 w-[22%]">
                    Ingrédient
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 w-[12%]">
                    Certitude
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                    Proposition CIQUAL
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 w-[18%]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-8 text-center text-sm text-gray-500">
                      Aucun item en attente pour ce filtre.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.ingredientId}>
                      <td className="px-3 py-2 align-top">
                        <div className="text-sm font-medium text-menioo-navy truncate">
                          {item.ingredientName}
                        </div>
                        <div className="text-xs text-gray-400">{item.language || '—'}</div>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${CONFIDENCE_STYLES[item.confidence]}`}
                        >
                          {CONFIDENCE_LABELS[item.confidence]}
                        </span>
                        {item.bestScore != null && (
                          <div className="text-xs text-gray-400 mt-1">{item.bestScore}%</div>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {item.candidates.length === 0 ? (
                          <span className="text-sm text-gray-400">Aucun candidat</span>
                        ) : (
                          <select
                            value={selections[item.ingredientId] || ''}
                            onChange={(e) =>
                              setSelections((prev) => ({
                                ...prev,
                                [item.ingredientId]: e.target.value,
                              }))
                            }
                            className="w-full rounded-md border-gray-300 text-sm"
                          >
                            {item.candidates.map((c) => (
                              <option key={c.ciqualCode} value={String(c.ciqualCode)}>
                                {c.nameRaw}
                                {c.preparationState ? ` · ${c.preparationState}` : ''} ({c.score}
                                %)
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top text-right whitespace-nowrap">
                        <button
                          type="button"
                          disabled={!!busy || item.candidates.length === 0}
                          onClick={() => applyOne(item)}
                          className="inline-flex items-center px-2.5 py-1.5 rounded text-xs font-medium text-white bg-menioo-green hover:bg-menioo-green-dark disabled:opacity-50"
                        >
                          {busy === `apply-${item.ingredientId}` ? (
                            <Loader className="h-3 w-3 animate-spin" />
                          ) : (
                            'Valider'
                          )}
                        </button>
                        <button
                          type="button"
                          disabled={!!busy}
                          onClick={() => skipOne(item)}
                          className="ml-2 text-xs text-gray-400 underline disabled:opacity-50"
                        >
                          Ignorer
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
